import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { useLayout } from "@/contexts/LayoutContext";
import { NewTaskButton } from "./NewTaskButton";
import { SearchBar } from "./SearchBar";
import { SelectionsDropdown } from "./SelectionsDropdown";
import { NotificationsHub } from "./notifications";
import { UserMenu } from "./UserMenu";
import { AskVectaButton } from "./AskVectaButton";
import { KlipboardIcon } from "@/components/logos/KlipboardIcon";
import { KlipboardCrmInline } from "@/components/logos/KlipboardCrmInline";

export const TopBar = () => {
  const { data, toggleSideNav, sideNavState, toggleMobileNav } = useLayout();
  const routes = data.appRoutes;
  const isMini = sideNavState === "mini";

  // isMiniLogo: true when side nav is mini OR on small screens (< 1024px, handled via CSS)
  const showMiniOnly = isMini;

  const [failedFullLogoUrl, setFailedFullLogoUrl] = useState<string | null>(null);
  const [failedMiniLogoUrl, setFailedMiniLogoUrl] = useState<string | null>(null);

  return (
    <nav
      role="navigation"
      className="layout-top-bar"
      style={{ backgroundColor: "var(--layout-top-nav-bg)" }}
    >
      <div className="flex items-center w-full pr-2 gap-1">
        {/* Brand logo — full when wide desktop, mini when nav is mini or screen < 1024px */}
        <a
          href={routes.myHome}
          className={`layout-logo-link flex items-center shrink-0 h-14.5 mr-1 w-12 ${showMiniOnly ? "" : "min-[1024px]:w-45.5"}`}
          aria-label="Home"
        >
          {/* Full logo: hidden on small screens or when mini */}
          {!showMiniOnly && (
            <span className="hidden min-[1024px]:flex items-center self-start ml-2.5 w-55 h-12">
              {data.fullLogoUrl && data.fullLogoUrl !== failedFullLogoUrl
                ? <img src={data.fullLogoUrl} alt="Logo" style={{ width: 220, height: 48, objectFit: "contain" }} onError={() => setFailedFullLogoUrl(data.fullLogoUrl)} />
                : <KlipboardCrmInline iconSize={26} />}
            </span>
          )}
          {/* Mini logo: always shown when mini, or on small screens */}
          <span className={`w-12 h-12 items-center justify-center ${showMiniOnly ? "flex" : "flex min-[1024px]:hidden"}`}>
            {data.miniLogoUrl && data.miniLogoUrl !== failedMiniLogoUrl
              ? <img src={data.miniLogoUrl} alt="Logo" style={{ width: 48, height: 48, objectFit: "contain" }} onError={() => setFailedMiniLogoUrl(data.miniLogoUrl)} />
              : <KlipboardIcon size={38} color="white" />}
          </span>
        </a>

        {/* Hamburger toggle (wide ↔ mini) — desktop only */}
        <button
          className="hidden lg:flex items-center justify-center w-9 h-14.5 text-white hover:bg-(--layout-brand-hover) transition-colors focus:outline-none shrink-0"
          style={{ marginLeft: isMini ? 0 : 40 }}
          onClick={toggleSideNav}
          aria-label="Toggle navigation"
        >
          <FontAwesomeIcon icon={faBars} size="xl" />
        </button>

        {/* Right-aligned items */}
        <div className="flex items-center ml-auto gap-4 md:gap-6">
          <NewTaskButton />
          {/* SearchBar — hidden on mobile/tablet portrait (moved into side nav) */}
          <div className="hidden md:flex">
            <SearchBar />
          </div>
          <SelectionsDropdown />
          {data.isAiChatAvailable && <AskVectaButton />}
          <NotificationsHub />
          {/* User avatar — hidden on very small mobile (<375px); moved to side nav bottom */}
          <div className="max-[374px]:hidden">
            <UserMenu />
          </div>
          {/* Mobile hamburger — far right, opens side nav drawer */}
          <button
            className="lg:hidden flex items-center justify-center w-10 h-14.5 text-white hover:bg-(--layout-brand-hover) transition-colors focus:outline-none"
            onClick={toggleMobileNav}
            aria-label="Toggle mobile navigation"
          >
            <FontAwesomeIcon icon={faBars} size="lg" />
          </button>
        </div>
      </div>
    </nav>
  );
}
